import { useState, useEffect } from 'react';
import { useAuth } from '../utils/AuthContext';
import { getDataOnRealtimeDb, saveDataOnRealtimeDb, updateDataOnRealtimeDb, deleteDataOnRealtimeDb } from '../utils/firebase';
import { toast } from 'react-toastify';
import moment from 'moment';

export const useIncome = () => {
    const [incomes, setIncomes] = useState([]);
    const [sources, setSources] = useState(null);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        total: 0,
        recurring: 0,
        oneTime: 0,
        monthlyStats: {}
    });
    const { currentUser } = useAuth();

    useEffect(() => {
        fetchIncomes();
        fetchSources();
    }, [currentUser]);

    const fetchIncomes = async () => {
        try {
            const dbIncomes = await getDataOnRealtimeDb(`users/${currentUser.uid}/incomes`);
            if (!dbIncomes) {
                setIncomes([]);
                setLoading(false);
                return;
            }

            const incomesArray = Object.keys(dbIncomes).map(key => ({
                id: key,
                name: dbIncomes[key].name,
                amount: dbIncomes[key].amount,
                source: dbIncomes[key].source,
                date: dbIncomes[key].date,
                description: dbIncomes[key].description,
                recurring: dbIncomes[key].recurring,
            }));

            setIncomes(incomesArray);
            calculateStats(incomesArray);
        } catch (error) {
            console.error("Error fetching incomes:", error);
            toast.error("Failed to load incomes");
        } finally {
            setLoading(false);
        }
    };

    const fetchSources = async () => {
        try {
            const sourcesData = await getDataOnRealtimeDb(`users/${currentUser.uid}/category/income`);
            if (!sourcesData) {
                setSources([]);
                return;
            }
            // Transform the data into the required format
            const dbSources = Object.entries(sourcesData).map(([key, value]) => ({
                id: key,
                Source: value
            })).reduce((acc, curr) => {
                acc.push(curr);
                return acc;
            }, []);

            setSources(dbSources[0]);
        } catch (error) {
            console.error("Error fetching sources:", error);
            toast.error("Failed to load income sources");
        }
    };

    const calculateStats = (incomeList) => {
        const total = incomeList.reduce((sum, inc) => sum + parseFloat(inc.amount || 0), 0);
        const recurring = incomeList.filter(inc => inc.recurring)
            .reduce((sum, inc) => sum + parseFloat(inc.amount || 0), 0);
        const oneTime = total - recurring;

        // Calculate monthly stats
        const monthlyStats = incomeList.reduce((acc, income) => {
            const month = moment(income.date).format('YYYY-MM');
            if (!acc[month]) {
                acc[month] = {
                    total: 0,
                    recurring: 0,
                    oneTime: 0
                };
            }
            const amount = parseFloat(income.amount || 0);
            acc[month].total += amount;
            if (income.recurring) {
                acc[month].recurring += amount;
            } else {
                acc[month].oneTime += amount;
            }
            return acc;
        }, {});

        setStats({ total, recurring, oneTime, monthlyStats });
    };

    const addIncome = async (values, { resetForm }) => {
        try {
            const { id, ...incomeToAdd } = values;
            incomeToAdd.amount = Math.abs(incomeToAdd.amount);

            await saveDataOnRealtimeDb(
                `users/${currentUser.uid}/incomes`, 
                incomeToAdd
            );

            await fetchIncomes();
            resetForm();
            return { success: true, message: "Income added successfully!" };
        } catch (error) {
            console.error('Error adding income:', error);
            return { success: false, message: "Failed to add income" };
        }
    };

    const updateIncome = async (values, { resetForm }) => {
        try {
            const { id, ...incomeToUpdate } = values;
            incomeToUpdate.amount = Math.abs(incomeToUpdate.amount);
            incomeToUpdate.recurring = Boolean(incomeToUpdate.recurring);

            await updateDataOnRealtimeDb(
                `users/${currentUser.uid}/incomes`, 
                id, 
                incomeToUpdate
            );

            await fetchIncomes();
            resetForm();
            return { success: true, message: "Income updated successfully!" };
        } catch (error) {
            console.error('Error updating income:', error);
            return { success: false, message: "Failed to update income" };
        }
    };

    const deleteIncome = async (id) => {
        try {
            await deleteDataOnRealtimeDb(`users/${currentUser.uid}/incomes`, id);
            await fetchIncomes();
            return { success: true, message: "Income deleted successfully!" };
        } catch (error) {
            console.error('Error deleting income:', error);
            return { success: false, message: "Failed to delete income" };
        }
    };

    const addSource = async (sourceName) => {
        try {
            const trimmedName = sourceName.trim();
            await saveDataOnRealtimeDb(
                `users/${currentUser.uid}/category/income`,
                { Category: trimmedName }
            );
            toast.success('Source added successfully!');
            await fetchSources();
            return true;
        } catch (error) {
            console.error('Error adding source:', error);
            toast.error('Failed to add source');
            return false;
        }
    };

    return {
        incomes,
        sources,
        loading,
        stats,
        addIncome,
        updateIncome,
        deleteIncome,
        addSource
    };
}; 